import React, { useRef, useState, useEffect } from 'react';
import update from 'immutability-helper';

import Appearance from 'styles/Appearance.js';
import DatePickerField from 'views/DatePickerField.js';
import ImagePickerField from 'views/ImagePickerField.js';
import { LayerItem } from 'structure/Layer.js';
import ListField from 'views/ListField.js';
import MultipleUserLookupField from 'views/MultipleUserLookupField.js';
import TextField from 'views/TextField.js';

export const validateRequiredFields = async get => {
    return new Promise((resolve, reject) => {
        let items = get().reduce((array, field) => {
            return array.concat(field.items);
        }, []);
        let required = items.find(item => {
            if(item.required === false) {
                return false;
            }
            return item.value === null || item.value === undefined;
        });
        if(required) {
            let error = new Error(`One or more required fields are incomplete. Please fill out the "${required.title.toLowerCase()}" before moving on`);
            reject(error);
            return;
        }
        resolve();
    })
}

const AltFieldMapper = ({ fields, utils }) => {

    const hoverEl = useRef(null);
    const [hover, setHover] = useState({});
    const [hoverSize, setHoverSize] = useState({
        width: 0,
        height: 0
    })

    const onMouseEnter = (key, evt) => {
        setHover({
            key: key,
            top: evt.target.offsetTop,
            left: evt.target.offsetLeft
        });
    }

    const onMouseLeave = () => {
        setHover({});
        setHoverSize({
            width: 0,
            height: 0
        })
    }

    const getComponent = item => {

        let { component, icon, items, loading, onChange, onValidate, props = {}, value } = item;
        switch(component) {

            case 'bool_list':
            return (
                <ListField
                items={[{
                    id: 'yes',
                    title: 'Yes'
                },{
                    id: 'no',
                    title: 'No'
                }]}
                onChange={item => onChange(item ? item.id === 'yes' : null)}
                value={typeof(value) === 'boolean' ? (value ? 'Yes' : 'No') : null}
                {...props} />
            );

            case 'date_picker':
            return (
                <DatePickerField
                utils={utils}
                icon={icon}
                value={value}
                selected={value}
                onValidate={onValidate}
                onDateChange={onChange}
                {...props} />
            )

            case 'image_picker':
            return (
                <ImagePickerField
                utils={utils}
                value={value}
                onChange={onChange}
                {...props} />
            )


            case 'list':
            return (
                <ListField
                items={items}
                onChange={onChange}
                value={value}
                {...props} />
            )

            case 'multiple_user_lookup':
            return (
                <MultipleUserLookupField
                onChange={onChange}
                users={value}
                utils={utils}
                {...props} />
            )

            case 'textfield':
            return (
                <TextField
                icon={icon}
                value={value}
                loading={loading}
                onValidate={onValidate}
                onChange={onChange}
                {...props} />
            )

            case 'textview':
            return (
                <TextField
                icon={icon}
                value={value}
                loading={loading}
                expandWithText={true}
                onValidate={onValidate}
                onChange={onChange}
                {...props} />
            )

            default:
            return null;
        }
    }

    useEffect(() => {
        if(hoverEl.current) {
            setHoverSize(props => update(props, {
                width: {
                    $set: hoverEl.current.clientWidth
                },
                height: {
                    $set: hoverEl.current.clientHeight
                }
            }))
        }
    }, [hover, hoverEl.current])

    return fields.filter(section => {
        return section.visible !== false
    }).map((section, index, sections) => {
        return (
            <LayerItem
            key={index}
            lastItem={index === sections.length - 1}
            rightContent={section.rightContent}
            title={section.title}
            collapsed={section.collapsed !== undefined ? section.collapsed : false}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%',
                    marginBottom: 15,
                    padding: 12,
                    paddingTop: 8, // accounts for text line-height
                    overflow: 'visible'
                }}>
                    {section.items && section.items.filter(item => {
                        return item.visible !== false;
                    }).map((item, index, items) => {
                        return (
                            <div
                            key={index}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                marginBottom: index !== items.length - 1 ? 12 : 0
                            }}>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center'
                                }}>
                                    {item.required !== false && (item.value === null || item.value === undefined) && (
                                        <div style={{
                                            width: 5,
                                            height: 5,
                                            borderRadius: 2.5,
                                            overflow: 'hidden',
                                            backgroundColor: Appearance.colors.red,
                                            marginRight: 8
                                        }} />
                                    )}
                                    <span style={{
                                        ...Appearance.textStyles.subHeader(),
                                        display: 'block',
                                        marginBottom: 4
                                    }}>{item.title}</span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    width: '100%',
                                    position: 'relative'
                                }}>
                                    {getComponent(item)}
                                    {item.description && (
                                        <img
                                        className={'text-button'}
                                        src={'images/help-button-grey.png'}
                                        onMouseEnter={onMouseEnter.bind(this, item.key)}
                                        onMouseLeave={onMouseLeave}
                                        style={{
                                            width: 20,
                                            height: 20,
                                            minWidth: 20,
                                            minHeight: 20,
                                            marginLeft: 12,
                                            objectFit: 'contain'
                                        }} />
                                    )}
                                    {hover.key === item.key && (
                                        <div
                                        ref={hoverEl}
                                        style={{
                                            ...Appearance.styles.unstyledPanel(),
                                            position: 'absolute',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            top: hoverSize.height ? (hover.top + 10 - (hoverSize.height / 2)) : 0,
                                            left: hoverSize.width ? (hover.left - hoverSize.width - 10) : 0,
                                            padding: '8px 12px 8px 12px',
                                            width: 'auto',
                                            maxWidth: 250,
                                            zIndex: 9999,
                                            textAlign: 'right',
                                            opacity: hoverSize.width && hoverSize.height ? 1 : 0,
                                            boxShadow: window.theme === 'dark' ? '5px 5px 15px rgba(0,0,0,0.25), -5px -5px 15px rgba(85,85,85,0.25)' : '5px 5px 15px rgba(175,175,174,0.25), -5px -5px 15px rgba(255,255,255,0.75)'
                                        }}>
                                            <span style={{
                                                ...Appearance.textStyles.subTitle(),
                                                whiteSpace: 'wrap'
                                            }}>{item.description}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </LayerItem>
        )
    })
}

export default AltFieldMapper;
